import {
  formatByCountry,
  ellipsisString,
  formatDate
} from "@/app/infrastructures/misc/Utils";
import { PrintData } from "@/domain/entities/UpdateDelivery";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { AccountController } from "@/app/ui/controllers/AccountController";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import { createI18n } from "vue-i18n";

let doc: any;
let translate: any = {};

const willDrawCellData = (cell: any) => {
  if (cell.section === "body" && cell.column.dataKey === "sttNo") {
    const text = String(cell.cell.raw ?? "");
    const baseX = cell.cell.x + 2;
    const baseY = cell.cell.y + cell.cell.height / 2 + 1;

    // Bagi teks → 2 depan, tengah, 4 belakang
    const prefix = text.slice(0, 2);
    const middle = text.slice(2, -4);
    const suffix = text.slice(-4);

    let cursorX = baseX;

    // --- 2 huruf depan (BOLD) ---
    doc.setFont("Poppins", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(prefix, cursorX, baseY);
    cursorX += doc.getTextWidth(prefix);

    // --- Tengah (NORMAL) ---
    doc.setFont("Poppins", "normal");
    doc.setFontSize(6);
    doc.setTextColor(80, 80, 80);
    doc.text(middle, cursorX, baseY);
    cursorX += doc.getTextWidth(middle);

    // --- 4 huruf belakang (BOLD) ---
    doc.setFont("Poppins", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(suffix, cursorX, baseY);

    cell.cell.text = [];
  }
};

const manifestPdf = async (dataHead: PrintData, data: any[], logo: string) => {
  doc = new jsPDF("p", "mm", "a4", true);
  const pdfWidth = doc.internal.pageSize.width;
  const logoProperties = doc.getImageProperties(logo);
  const logoAspectRatio = logoProperties.height / logoProperties.width;

  const i18nInstance = createI18n({
    legacy: false,
    locale:
      AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
    fallbackLocale: "id",
    globalInjection: false,
    messages: {
      en,
      id,
      my
    }
  });
  translate = i18nInstance.global.t;

  const imageWidth = 40;
  const imageHeight = logoAspectRatio * 40;
  doc.addImage(
    logo,
    "PNG",
    pdfWidth - imageWidth - 9,
    11,
    imageWidth,
    imageHeight,
    "logo",
    "FAST",
    0
  );
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor("#4d4d4d");
  doc.text("Delivery Manifest", 10, 18);
  doc.setFillColor("#e0e0e0");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  // left column
  doc.setTextColor("#4d4d4d");
  doc.setFillColor("#ebecf0");

  doc.text(translate("deliveryManifest.pdf.consolidatorCity"), 10, 35);
  doc.text(
    `: ${dataHead.deliveryMasterDestinationCityCode} - ${dataHead.deliveryMasterDestinationCityName}`,
    38,
    35
  );

  doc.text(translate("deliveryManifest.pdf.createdBy"), 10, 42);
  doc.text(
    `: ${dataHead.deliveryMasterPartnerType} - ${ellipsisString(
      dataHead.deliveryMasterPartnerName,
      23
    )}`,
    38,
    42
  );

  // center column
  doc.setTextColor("#4d4d4d");
  doc.setFillColor("#ebecf0");

  doc.text(translate("deliveryManifest.pdf.totalWeight"), 95, 35);
  doc.text(
    `: ${formatByCountry(
      dataHead.deliveryMasterTotalGrossWeight,
      { type: 'weight', country: AccountController.accountData.account_type_detail?.countryCode, fractionDigits: 2 }
    )}kg`,
    115,
    35
  );

  doc.text(translate("deliveryManifest.pdf.totalSTTPieces"), 95, 42);
  doc.text(
    `: ${dataHead.deliveryMasterTotalStt}/${dataHead.deliveryMasterTotalPiece}`,
    115,
    42
  );

  // right column
  doc.setTextColor("#4d4d4d");
  doc.setFillColor("#ebecf0");

  doc.text(translate("deliveryManifest.pdf.manifestDate"), 133, 35);
  doc.text(
    `: ${formatDate(
      dataHead.deliveryMasterCreatedAt,
      "DD MMMM YYYY, HH.mm",
      true
    )}`,
    160,
    35
  );

  doc.text(translate("deliveryManifest.pdf.courier"), 133, 42);
  doc.text(
    `: ${ellipsisString(dataHead.deliveryMasterDriverName, 30)}`,
    160,
    42
  );
  doc.text(dataHead.deliveryMasterDriverPhone, 161.5, 46);

  let lastPage = 0;
  let lastY = 0;
  let lastHeight = 0;
  const dataLength = data.length;
  doc.autoTable({
    body: data,
    columns: [
      { header: "No.", dataKey: "no" },
      {
        header: translate("deliveryManifest.column.sttNo"),
        dataKey: "sttNo"
      },
      { header: translate("deliveryManifest.column.referenceNo"), dataKey: "refNo" },
      { header: translate("deliveryManifest.column.product"), dataKey: "productType" },
      { header: translate("deliveryManifest.column.deliveryType"), dataKey: "service" },
      { header: translate("deliveryManifest.column.piecesWeigght"), dataKey: "piecePerWeight" },
      { header: translate("deliveryManifest.column.demNo"), dataKey: "demNo" },
      { header: "Ursa code", dataKey: "destinationUrsaCode" }
    ],
    theme: "plain",
    startY: doc.internal.getCurrentPageInfo().pageNumber === 1 ? 52 : 0,
    margin: { top: 7, left: 10, right: 10, bottom: 7 },
    headStyles: {
      font: "helvetica",
      fontStyle: "bold",
      fillColor: "#f5f6f7",
      textColor: "#1a1421",
      fontSize: 6,
      halign: "left",
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 }
    },
    bodyStyles: {
      font: "helvetica",
      fontStyle: "normal",
      textColor: "#1a1421",
      fontSize: 7,
      halign: "left",
      valign: "top",
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 },
      cellWidth: "auto"
    },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 29 },
      2: { cellWidth: 28 },
      3: { cellWidth: 30 },
      4: { cellWidth: 22 },
      5: { cellWidth: 21 },
      6: { cellWidth: 29 },
      7: { cellWidth: 24 }
    },
    willDrawCell: (cell: any) => {
      willDrawCellData(cell);
      lastPage = cell.pageNumber;
      lastY = cell.cursor.y;
      lastHeight = cell.row.height;
      if (cell.row.index === dataLength - 2 && lastY + lastHeight + 20 > 270) {
        cell.cell.contentHeight = 100;
        cell.row.height = 100;
      }
    }
  });
  lastY = lastY + lastHeight + 20;
  lastY = lastY > 270 ? 270 : lastY;
  doc.setPage(lastPage);
  doc.setFontSize(8);
  doc.text(translate("deliveryManifest.pdf.courier"), 50 + 90, lastY);
  doc.text("(.................................)", 40 + 90, lastY + 20);
  doc.text("Operations Supervisor", 80 + 90, lastY);
  doc.text("(.................................)", 80 + 90, lastY + 20);

  doc.setProperties({
    title: "Delivery Manifest"
  });

  doc.autoPrint();

  window.open(doc.output("bloburl"), "_blank");
};

export default manifestPdf;
